/** One brace group, allowing one level of nesting (e.g. `{500{,}000}`). */
const BRACE_ARG = String.raw`\{(?:[^{}]|\{[^{}]*\})*\}`;
/** TeX command with optional brace args: `\dfrac{a}{b}`, `\approx`, `\text{Rs }`. */
const TEX_CMD = String.raw`\\[a-zA-Z]+\*?(?:${BRACE_ARG})*`;
/** Thin/med/thick space or escaped percent/dollar. */
const TEX_ESC = String.raw`\\[,;!% $]`;
/**
 * Number that may use TeX thousands separators (`38{,}461.54`) or model-style
 * ASCII commas (`1,231,008.65`) which we later normalize to `{,}`.
 */
const TEX_NUM = String.raw`[0-9]+(?:(?:\{,\}|,)[0-9]+)*(?:\.[0-9]+)?`;
const TEX_TOKEN = String.raw`(?:${TEX_CMD}|${TEX_ESC}|${TEX_NUM}|[+\-=()≈×·÷/^_])`;
/**
 * Bare inline TeX: starts with a command, then more math tokens.
 * Example: `\dfrac{500{,}000}{13} \approx 38{,}461.54`
 */
const BARE_TEX_RE = new RegExp(`${TEX_CMD}(?:\\s*${TEX_TOKEN})*`, 'g');

/**
 * Escapes bare `%` in TeX (comment char) so KaTeX does not truncate formulas
 * like `\text{Tax at 10%}`. Leaves already-escaped `\%` alone.
 */
export function escapeBarePercentInTex(tex: string): string {
  return tex.replace(/(?<!\\)%/g, '\\%');
}

/**
 * Convert model-style thousands commas to TeX `{,}` so KaTeX does not treat
 * commas as list punctuation inside formulas (`1,231.5` → `1{,}231.5`).
 * Skips commas already written as `{,}`.
 */
export function normalizeThousandsCommasInTex(tex: string): string {
  return tex.replace(/(\d),(?=\d)/g, '$1{,}');
}

/** Drop orphan trailing backslashes the model often leaves after `\%`. */
export function stripTrailingOrphanBackslashes(tex: string): string {
  return tex.replace(/\\+$/g, (run) => {
    // Keep even-length pairs that could be intentional `\\` line breaks; drop a lone `\`.
    return run.length % 2 === 0 ? run : run.slice(0, -1);
  });
}

function prepareTexBody(tex: string): string {
  // Collapse newlines — multiline $$ bodies otherwise stay raw in marked-katex.
  let out = tex.replace(/\s*\n\s*/g, ' ').trim();
  out = escapeBarePercentInTex(out);
  out = normalizeThousandsCommasInTex(out);
  out = stripTrailingOrphanBackslashes(out);
  // KaTeX is happier with a space between \times and a following command.
  out = out.replace(/\\times(?=\\[a-zA-Z])/g, '\\times ');
  return out;
}

function protectMathRegions(text: string): { text: string; slots: string[] } {
  const slots: string[] = [];
  const replaced = text
    .replace(/\$\$([\s\S]+?)\$\$/g, (_full, body: string) => {
      const i = slots.length;
      slots.push(`$$${prepareTexBody(body)}$$`);
      return `\0MATH${i}\0`;
    })
    .replace(/(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g, (_full, body: string) => {
      const i = slots.length;
      slots.push(`$${prepareTexBody(body)}$`);
      return `\0MATH${i}\0`;
    });
  return { text: replaced, slots };
}

function restoreMathRegions(text: string, slots: string[]): string {
  return text.replace(/\0MATH(\d+)\0/g, (_m, idx: string) => slots[Number(idx)] ?? '');
}

/**
 * Wraps undelimited TeX (common in model table cells) in `$...$` so KaTeX runs.
 * Skips regions already inside `$` / `$$`.
 */
export function wrapBareTexInMarkdown(text: string): string {
  if (!text.includes('\\')) return text;
  const { text: protectedText, slots } = protectMathRegions(text);
  const wrapped = protectedText.replace(BARE_TEX_RE, (match) => {
    // Avoid wrapping leftover backslashes that are not real math.
    if (!/\\[a-zA-Z]/.test(match)) return match;
    // Keep trailing ) outside $...$ when the opening ( was prose (e.g. "Rs ( \dfrac... )").
    const trailingParens = match.match(/\)+$/)?.[0] ?? '';
    const core = match.slice(0, match.length - trailingParens.length).trim();
    if (!core) return match;
    return `$${prepareTexBody(core)}$${trailingParens}`;
  });
  // Orphan `\` often left after bare `\%\` outside any match.
  const cleaned = wrapped.replace(/(?<!\\)\\(?![a-zA-Z{},;!% $\\])/g, '');
  return restoreMathRegions(cleaned, slots);
}

/**
 * Collapse multiline $$...$$ to a single-line fence.
 * marked-katex leaves raw `$$` when display math spans lines without blank-line isolation.
 */
export function flattenDisplayMath(text: string): string {
  if (!text.includes('$$')) return text;
  return text.replace(
    /\$\$([\s\S]+?)\$\$/g,
    (_full, body: string) => `$$${prepareTexBody(body)}$$`
  );
}

function sanitizeMathRegions(text: string): string {
  let out = flattenDisplayMath(text);
  // Wrap bare TeX (also escapes % / normalizes commas inside newly wrapped math).
  out = wrapBareTexInMarkdown(out);
  if (!out.includes('$')) return out;

  // Re-prepare any remaining / already-delimited math.
  out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_full, body: string) => {
    return `$$${prepareTexBody(body)}$$`;
  });
  out = out.replace(/(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g, (_full, body: string) => {
    return `$${prepareTexBody(body)}$`;
  });
  return out;
}

/**
 * Fixes common model LaTeX mistakes in markdown before KaTeX runs.
 * - Escapes bare `%` inside `$...$` / `$$...$$`
 * - Normalizes ASCII thousands commas to `{,}`
 * - Strips orphan trailing `\`
 * - Flattens multiline display math to single-line `$$...$$`
 * - Wraps undelimited TeX commands (e.g. `\dfrac{...}` in tables) in `$...$`
 * Skips fenced code so charts and snippets stay intact.
 */
export function sanitizeLatexInMarkdown(markdown: string): string {
  if (!markdown.includes('\\') && !markdown.includes('%') && !markdown.includes('$$')) {
    return markdown;
  }

  let result = '';
  let lastIndex = 0;
  const fenceRe = /```[\s\S]*?(?:```|$)/g;
  let match: RegExpExecArray | null;
  while ((match = fenceRe.exec(markdown)) !== null) {
    result += sanitizeMathRegions(markdown.slice(lastIndex, match.index));
    result += match[0];
    lastIndex = match.index + match[0].length;
  }
  result += sanitizeMathRegions(markdown.slice(lastIndex));
  return result;
}
